import clsx from 'clsx';
import { CheckCircleIcon, TrashIcon } from '@heroicons/react/24/solid';
import Avatar from '@/components/Avatar';
import type { Poll, PollOption } from '@flock-types/polls.types';

interface Props {
  option: PollOption;
  poll: Poll;
  totalVotes: number;
  canSeeResults: boolean;
  hasVoted: boolean;
  isSelected: boolean;
  /** True while the user is still choosing (not voted, poll open). */
  isSelecting: boolean;
  allowMultiple: boolean;
  onToggle: () => void;
  /** Leader-only option delete; undefined hides the affordance. */
  onDelete?: () => void;
}

/** Option row: selector, result bar + %, voter avatars (Expo OptionRow port). */
export default function PollOptionRow({
  option,
  poll,
  totalVotes,
  canSeeResults,
  hasVoted,
  isSelected,
  isSelecting,
  allowMultiple,
  onToggle,
  onDelete,
}: Props) {
  // vote_count is absent for non-leaders on hidden-results active polls
  const voteCount = option.vote_count ?? 0;
  const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;

  return (
    <div
      onClick={isSelecting ? onToggle : undefined}
      role={isSelecting ? 'button' : undefined}
      className={clsx(
        'group relative rounded-xl border bg-white p-4 transition',
        isSelected ? 'border-2 border-flock-navy-600' : 'border-flock-gray-200',
        isSelecting && 'cursor-pointer hover:border-flock-navy-400'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Selection indicator */}
        {isSelecting && (
          <span
            className={clsx(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border-2 transition',
              allowMultiple ? 'rounded' : 'rounded-full',
              isSelected ? 'border-flock-navy-600 bg-flock-navy-600' : 'border-flock-navy-300'
            )}
          >
            {isSelected &&
              (allowMultiple ? (
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 fill-white">
                  <path d="M6.5 11.5 3 8l1.06-1.06L6.5 9.38l5.44-5.44L13 5z" />
                </svg>
              ) : (
                <span className="h-2 w-2 rounded-full bg-white" />
              ))}
          </span>
        )}

        {/* Voted checkmark */}
        {hasVoted && isSelected && !isSelecting && (
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
        )}

        <div className="min-w-0 flex-1">
          <div className="text-base font-medium text-flock-navy-800">{option.label}</div>

          {canSeeResults && (
            <div className="mt-2">
              <div className="h-2 overflow-hidden rounded-full bg-flock-gray-200">
                <div
                  className="h-2 rounded-full bg-flock-navy-600 transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between text-xs text-flock-navy-500">
                <span>
                  {voteCount} vote{voteCount !== 1 ? 's' : ''}
                </span>
                <span className="font-medium">{percentage}%</span>
              </div>
            </div>
          )}

          {canSeeResults && !poll.is_anonymous && option.voters && option.voters.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
              {option.voters.map((voter) => (
                <span key={voter.user_id} className="flex items-center gap-1.5">
                  <Avatar
                    src={voter.user_profile_photo}
                    name={voter.user_name}
                    sizeClass="h-5 w-5"
                    textClass="text-[9px]"
                    bgClass="bg-flock-navy-500"
                  />
                  <span className="text-xs text-flock-navy-500">{voter.user_name}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Delete option"
            className="shrink-0 rounded p-1 text-flock-navy-300 opacity-0 transition hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
